import React, { useState } from 'react'
import {
	Message as ChatUIKit_Message
} from "@chatscope/chat-ui-kit-react"

import { Message as HAMessage } from '../../../../shared-src/message'

import { getColorSet, hexForTwColor } from '@/theme/color'
import { useChatStore } from "@/state/chat-store"
import { ChannelType } from '../../../../shared-src/channel'
import { MessageText } from "./message-text"
import { MessageReactions } from "./message-reactions"
import { ImageViewer } from "@/components/image-viewer"
import { User } from '../../../../shared-src/user'
import { AddReactionButton } from './add-reaction-button'

interface MessageProps {
	message: HAMessage
	selfUser: User | null
	showSender: boolean
	isLastMessage: boolean
}

export function Message(props: MessageProps) {
	const channel = useChatStore(state => state.activeChannel)
	const [showImageViewer, setShowImageViewer] = useState(false)

	if (!channel)
		return null

	const sender = channel.details?.membersByUserId[props.message.senderId]
	const senderColor = sender?.color ?? props.message.senderColor
	const senderColorSet = getColorSet({ senderType: props.message.senderType, senderColor })
	const selfUserColorSet = getColorSet({ senderColor: props.selfUser?.color })
	const isMyMessage = props.selfUser?.userId === props.message.senderId

	// const senderName = props.message.senderType === 'system'
	// 	? 'hangaround.live'
	// 	: props.message.senderDeleted
	// 		? '[Deleted User]'
	// 		: (sender?.name ?? props.message.senderName ?? props.message.senderId ?? 'unknown')

	let senderName = 'unknown'
	if (props.message.senderId)
		senderName = props.message.senderId
	if (props.message.senderName)
		senderName = props.message.senderName
	if (sender?.name)
		senderName = sender.name
	if (props.message.senderDeleted)
		senderName = '[Deleted User]'
	if (props.message.senderType === 'system')
		senderName = 'hangaround.live'

	let badgeText: string | undefined = undefined
	let badgeColor: string | undefined = undefined

	if (props.message.senderType === 'bot') {
		badgeText = 'bot'
		badgeColor = 'lime'
	}
	if (props.message.senderType === 'admin') {
		badgeText = 'dev'
		badgeColor = 'rose'
	}
	if (props.message.senderType === 'system') {
		badgeText = 'sys'
		badgeColor = 'rose'
	}
	if (badgeColor)
		badgeColor = hexForTwColor(`${badgeColor}-600`)

	const isImage = props.message.attachment?.previewUrl ? true : false
	const imageUrl = props.message.attachment?.downloadUrl || props.message.attachment?.previewUrl

	let showSenderUserId = sender?.includeUserIdInSender
	if (channel.type === ChannelType.public && senderName !== props.message.senderId)
		showSenderUserId = true
	if (props.message.senderType === 'system' || props.message.senderType === 'bot' || props.message.senderType === 'admin')
		showSenderUserId = false
	
	const canUseReactions = channel.usersCanReact

	return (
		<div className={`${props.isLastMessage ? 'mb-4' : 'mb-2'}`}>			
			{ props.showSender && 
				<div 
					className={`pt-4 text-sm font-bold ${isMyMessage ? 'text-right' : 'text-left'}`}
					style={{ color: senderColorSet.dark }}
				>
					{senderName}
					{showSenderUserId && (
						<span className="ml-1 text-sm">
							({props.message.senderId})
						</span>
					)}
					{badgeText && (
						<span className="ml-1 px-2 py-0.5 text-[9px] text-white rounded-full align-top"
							style={{ backgroundColor: badgeColor }}
						>
							{badgeText}
						</span>
					)}
				</div> 
			}
			<div className={`flex items-center gap-2 ${isMyMessage ? 'justify-end' : 'justify-start'}`}>
				<div className="flex-shrink min-w-0 max-w-[90%]">
					<div className="flex items-center gap-2">
						{/* spacer */}
						{ isMyMessage && (<div className="flex-1"/>)}

						{ canUseReactions && isMyMessage && (
							<AddReactionButton
								messageUlid={props.message.ulid}
								channelId={channel.id}
								selfUser={props.selfUser}
								selfUserColorSet={selfUserColorSet}
							/>
						)}

						<ChatUIKit_Message
							type="custom"
							model={{
								direction: isMyMessage ? "outgoing" : "incoming",
								position: "single",
								sender: props.message.senderName
							}}
							style={{
								'--message-bg-color': senderColorSet.background,
								'--message-border-color': senderColorSet.border,
								'--message-text-color': senderColorSet.text
							} as React.CSSProperties}
						>
							{isImage &&
								<ChatUIKit_Message.CustomContent>
									<img
										src={props.message.attachment?.previewUrl}
										className="rounded-md cursor-pointer hover:opacity-90 transition-opacity"
										onClick={() => setShowImageViewer(true)}
									/>
								</ChatUIKit_Message.CustomContent>
							}
							{!isImage && props.message.text &&
								<ChatUIKit_Message.CustomContent className="text-sm">
									<MessageText text={props.message.text} />
								</ChatUIKit_Message.CustomContent>
							}
						</ChatUIKit_Message>

						{ canUseReactions && !isMyMessage && (
							<AddReactionButton
								messageUlid={props.message.ulid}
								channelId={channel.id}
								selfUser={props.selfUser}
								selfUserColorSet={selfUserColorSet}
							/>
						)}

						{/* spacer */}
						{ !isMyMessage && (<div className="flex-1"/>)}
					</div>

					{canUseReactions && (
						<MessageReactions
							messageUlid={props.message.ulid}
							channelId={channel.id}
							reactions={props.message.reactions}
							selfUser={props.selfUser}
							selfUserColorSet={selfUserColorSet}
							senderColorSet={senderColorSet}
							isMyMessage={isMyMessage}
						/>
					)}
				</div>
			</div>

			{isImage && imageUrl && (
				<ImageViewer
					open={showImageViewer}
					onOpenChange={setShowImageViewer}
					imageUrl={imageUrl}
					altText={`Image from ${senderName}`}
				/>
			)}
		</div>
	)
}

